import {
  AttributeForm,
  ClaimOwnerCapacity,
  EditEventForm,
  editEventFormDefault,
  editEventFormSchema,
  EditScheduleForm,
  editScheduleFormDefault,
  editScheduleFormSchema,
  ZonesInformation,
  GeneralInformation,
  LocationFormBulk,
  MultiSelectOption,
  RenderBlockedTimeSectionProps,
  RenderWorkdaySectionProps,
  SubAttribute,
  TeamsOfficesRoles,
} from '@dispatch/shared/types';
import styled from '@emotion/styled';
import {
  Box,
  Card,
  CardContent,
  Divider,
  Stack,
  Typography,
} from '@mui/material';
import { format } from 'date-fns';
import { isEmpty, isUndefined } from 'lodash';
import { allFieldsUndefined, CompanyOptions } from '../BulkUpdateResource';
import {
  CreateNewEntityButton,
  Form,
  Modal,
  RenderFuncProps,
  SectionWithMeta,
} from '@dispatch/web/ui';
import { EditSchedule } from './General/EditSchedule';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { EditEvent } from './General/EditEvent';
import React from 'react';

export function scheduleOverlaps(
  candidateSchedule: EditScheduleForm,
  existingSchedule: EditScheduleForm
): boolean {
  const candidateStart = new Date(candidateSchedule.startDateTime);
  const candidateEnd = new Date(candidateSchedule.endDateTime);
  const existingStart = new Date(existingSchedule.startDateTime);
  const existingEnd = new Date(existingSchedule.endDateTime);
  // <= is intentional: schedules sharing an exact boundary point are treated as overlapping,
  // consistent with ResourceScheduleHelper's day-granularity duplicate detection.
  return candidateStart <= existingEnd && existingStart <= candidateEnd;
}

export function accumulateEvents(
  currentEvents: EditEventForm[] | undefined,
  newEvent: EditEventForm
): EditEventForm[] {
  return currentEvents ? [...currentEvents, newEvent] : [newEvent];
}

export const StyledP = styled.p`
  margin: 0;
  padding: 0;
`;

export const StyledH3 = styled.h3`
  margin: 0;
`;

export function renderAttributesChanges(attributes: AttributeForm[]) {
  return attributes.map((attribute) => {
    return (
      <Card
        variant="outlined"
        sx={{
          width: 320,
          overflow: 'auto',
          resize: 'horizontal',
        }}
      >
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
          sx={{
            padding: '5px 10px',
          }}
        >
          <Typography
            gutterBottom
            sx={{ color: 'text.secondary', fontSize: 14, fontWeight: 'bold' }}
          >
            {attribute?.displayName}
          </Typography>
          <></>
        </Stack>

        <CardContent sx={{ padding: '5px 10px' }}>
          {attribute?.selectedAttributeValuesVarchars?.map(
            (selectedAttribute, index: number) => {
              return (
                <div>
                  <Stack sx={{ padding: '0 10px' }}>
                    <StyledP>{`${selectedAttribute?.title}${
                      selectedAttribute?.startDate
                        ? `: ${format(
                            new Date(selectedAttribute?.startDate),
                            'MM/dd/yyyy'
                          )}`
                        : ''
                    } ${
                      selectedAttribute?.endDate
                        ? '-' +
                          format(
                            new Date(selectedAttribute?.endDate),
                            'MM/dd/yyyy'
                          )
                        : ''
                    }`}</StyledP>
                    <div style={{ marginLeft: '10px' }}>
                      {selectedAttribute?.subAttributes?.map(
                        (subAttribute: SubAttribute) => {
                          return (
                            <Stack direction="row">
                              <p style={{ margin: 0 }}>
                                {subAttribute?.displayName}
                              </p>
                              <div>
                                {subAttribute?.startDate && (
                                  <StyledP>
                                    {`${
                                      subAttribute?.startDate
                                        ? `: ${format(
                                            new Date(subAttribute?.startDate),
                                            'MM/dd/yyyy'
                                          )}`
                                        : ''
                                    } ${
                                      subAttribute?.endDate &&
                                      (subAttribute?.endDate
                                        ? ` - ${format(
                                            new Date(subAttribute?.endDate),
                                            'MM/dd/yyyy'
                                          )}`
                                        : '')
                                    }`}
                                  </StyledP>
                                )}
                              </div>
                            </Stack>
                          );
                        }
                      )}
                    </div>
                  </Stack>
                  {index !==
                    attribute?.selectedAttributeValuesVarchars.length - 1 && (
                    <Divider sx={{ marginY: '10px' }} />
                  )}
                </div>
              );
            }
          )}
        </CardContent>
      </Card>
    );
  });
}

export function renderGeneralInformationChanges(
  generalInformation: GeneralInformation
) {
  return (
    <Stack>
      {generalInformation?.commuteTimeInMin && (
        <StyledP style={{ margin: 0, padding: 0 }}>
          Allowable Commute Time: {generalInformation?.commuteTimeInMin}
        </StyledP>
      )}
      {generalInformation?.efficiency && (
        <StyledP style={{ margin: 0, padding: 0 }}>
          Efficiency: {generalInformation?.efficiency}
        </StyledP>
      )}
      {generalInformation?.wipLimit && (
        <StyledP style={{ margin: 0, padding: 0 }}>
          WIP Limit: {generalInformation?.wipLimit}
        </StyledP>
      )}
      {generalInformation?.isAlwaysAvailable && (
        <StyledP style={{ margin: 0, padding: 0 }}>
          Always Available:{' '}
          {generalInformation?.isAlwaysAvailable ? 'Yes' : 'No'}
        </StyledP>
      )}
      {generalInformation?.isWorkTeam && (
        <StyledP style={{ margin: 0, padding: 0 }}>
          Work Team: {generalInformation?.isWorkTeam ? 'Yes' : 'No'}
        </StyledP>
      )}
      {generalInformation?.lockImmediately && (
        <StyledP style={{ margin: 0, padding: 0 }}>
          Lock Tasks Immediately:{' '}
          {generalInformation?.lockImmediately ? 'Yes' : 'No'}
        </StyledP>
      )}
      {!isUndefined(generalInformation?.isStandby) && (
        <StyledP style={{ margin: 0, padding: 0 }}>
          Standby: {generalInformation?.isStandby ? 'Yes' : 'No'}
        </StyledP>
      )}
      {generalInformation?.claimCapacity && (
        <StyledP style={{ margin: 0, padding: 0 }}>
          Claim Capacity: {generalInformation?.claimCapacity}
        </StyledP>
      )}
      {generalInformation?.virtualClaimCapacity && (
        <StyledP style={{ margin: 0, padding: 0 }}>
          Virtual Claim Capacity: {generalInformation?.virtualClaimCapacity}
        </StyledP>
      )}
      {generalInformation?.complexLossClaimCapacity && (
        <StyledP style={{ margin: 0, padding: 0 }}>
          Complex Loss Claim Capacity:{' '}
          {generalInformation?.complexLossClaimCapacity}
        </StyledP>
      )}
    </Stack>
  );
}

export function renderClaimOwnerCapacityChanges(
  claimOwner: ClaimOwnerCapacity
) {
  return (
    <Stack>
      {claimOwner?.onSunday && (
        <StyledP>Sunday: {claimOwner?.onSunday}</StyledP>
      )}
      {claimOwner?.onMonday && (
        <StyledP>Monday: {claimOwner?.onMonday}</StyledP>
      )}
      {claimOwner?.onTuesday && (
        <StyledP>Tuesday: {claimOwner?.onTuesday}</StyledP>
      )}
      {claimOwner?.onWednesday && (
        <StyledP>Wednesday: {claimOwner?.onWednesday}</StyledP>
      )}
      {claimOwner?.onThursday && (
        <StyledP>Thursday: {claimOwner?.onThursday}</StyledP>
      )}
      {claimOwner?.onFriday && (
        <StyledP>Friday: {claimOwner?.onFriday}</StyledP>
      )}
      {claimOwner?.onSaturday && (
        <StyledP>Saturday: {claimOwner?.onSaturday}</StyledP>
      )}
      {claimOwner?.maxWeeklyCapacity && (
        <StyledP>Max: {claimOwner?.maxWeeklyCapacity}</StyledP>
      )}
    </Stack>
  );
}

export function renderTeamOfficesRolesChanges(
  data: TeamsOfficesRoles,
  companyOptions: CompanyOptions
) {
  if (isEmpty(data)) return null;
  const teams = data?.teams || [];
  const offices = data?.offices || [];
  const roles = data?.roles || [];

  const parseTeams = teams?.flatMap((team: string) => {
    return companyOptions?.Team?.flatMap((t: MultiSelectOption) => {
      if (t.value === team) {
        return t.title;
      }
      return [];
    });
  });

  const parseOffices = offices?.flatMap((office: string) => {
    return companyOptions?.Office?.flatMap((t: MultiSelectOption) => {
      if (t.value === office) {
        return t.title;
      }
      return [];
    });
  });

  const parseRoles = roles?.flatMap((role: string) => {
    return companyOptions?.Role?.flatMap((t: MultiSelectOption) => {
      if (t.value === role) {
        return t.title;
      }
      return [];
    });
  });

  return (
    <Stack>
      <StyledP>
        {data.isContingent &&
          `Contingent: ${String(data.isContingent) === 'true' ? 'Yes' : 'No'}`}
      </StyledP>
      <StyledP>
        {data.contingentCompany &&
          `Contingent Company: ${data.contingentCompany}`}
      </StyledP>
      <StyledP>
        {parseTeams?.length > 0 && `Teams: ${parseTeams.join(', ')}`}
      </StyledP>
      <StyledP>
        {parseOffices?.length > 0 && `Offices: ${parseOffices.join(', ')}`}
      </StyledP>
      <StyledP>
        {parseRoles?.length > 0 && `Roles: ${parseRoles.join(', ')}`}
      </StyledP>
    </Stack>
  );
}

export function renderLocationChanges(data: LocationFormBulk) {
  if (isEmpty(data)) return null;
  const locations = Object.values(data);
  const daysOfWeek = [
    'monday',
    'tuesday',
    'wednesday',
    'thursday',
    'friday',
    'saturday',
    'sunday',
  ];
  return (
    <Stack direction="row" gap={2} flexWrap="wrap">
      {locations.map((location, index: number) => {
        const activeDays: string[] = Object.entries(location.serviceInformation)
          .filter((d) => daysOfWeek?.includes(d?.[0]))
          .flatMap((d) => {
            if (d[1]) return d[0];
            return [];
          });

        const days = activeDays.map((d) => {
          const firstletter = d[0];
          return firstletter.toUpperCase() + d.slice(1, 3);
        });

        return (
          <Stack key={index}>
            <StyledH3>{location?.locationInformation?.name}</StyledH3>
            <Stack pl={1}>
              <StyledP>State: {location?.locationInformation?.state}</StyledP>
              <StyledP>Zip: {location?.locationInformation?.zip}</StyledP>
              <StyledP>
                Virtual Location:{' '}
                {String(location?.locationInformation?.isVirtualLocation) ===
                'true'
                  ? 'Yes'
                  : 'No'}
              </StyledP>
              <StyledP>
                Date:
                {` ${
                  location?.serviceInformation?.startDate &&
                  format(
                    new Date(location?.serviceInformation?.startDate),
                    'MM/dd/yyyy'
                  )
                }${
                  location?.serviceInformation?.endDate
                    ? ' - ' +
                      format(
                        new Date(location?.serviceInformation?.endDate),
                        'MM/dd/yyyy'
                      )
                    : ''
                }`}
              </StyledP>
              {location?.locationInformation?.isVirtualLocation === 'false' && (
                <StyledP>
                  Allowed Travel Radius:
                  {` ${location?.serviceInformation?.allowedTravelRadius}`}
                </StyledP>
              )}
              <StyledP>Days: {` ${days.join(', ')}`}</StyledP>
              {location?.exclusionZonesInformation &&
                !allFieldsUndefined(location?.exclusionZonesInformation) && (
                  <div>
                    <StyledH3>Exclusions </StyledH3>
                    <Stack direction="row" flexWrap="wrap" gap={2} px={1}>
                      {renderZoneInfoChanges({
                        ...location?.exclusionZonesInformation,
                      })}
                    </Stack>
                  </div>
                )}
              {location?.inclusionZonesInformation &&
                !allFieldsUndefined(location?.inclusionZonesInformation) && (
                  <div>
                    <StyledH3>Inclusions </StyledH3>
                    <Stack direction="row" flexWrap="wrap" gap={2} px={1}>
                      {renderZoneInfoChanges({
                        ...location?.inclusionZonesInformation,
                      })}
                    </Stack>
                  </div>
                )}
            </Stack>
          </Stack>
        );
      })}
    </Stack>
  );
}

export function renderZoneInfoChanges(data: ZonesInformation) {
  if (isEmpty(data)) return null;

  const counties = data?.counties as MultiSelectOption[];
  const zipCodes = data?.zipCodes || [];
  const states = data?.states || [];

  const parsedCounties = counties?.map((team) => {
    return team.title;
  });

  return (
    <Stack>
      <StyledP>
        {parsedCounties?.length > 0 && `Counties: ${parsedCounties.join(', ')}`}
      </StyledP>
      <StyledP>
        {zipCodes?.length > 0 && `Zip Codes: ${zipCodes.join(', ')}`}
      </StyledP>
      <StyledP>{states?.length > 0 && `States: ${states.join(', ')}`}</StyledP>
    </Stack>
  );
}

export const RenderWorkdaySection = ({
  hasUpsertSchedulesPermissions,
  handleOpenEditSchedule,
  renderAddedEvents,
  openEditSchedule,
  handleCloseEditSchedule,
  isEdit,
  permissions,
  resourceSetValue,
  resourceWatch,
}: RenderWorkdaySectionProps) => {
  const {
    control: editScheduleControl,
    formState: editScheduleFormState,
    handleSubmit: handleEditScheduleSubmit,
    reset: resetEditSchedule,
    setError: setEditScheduleError,
    clearErrors: clearEditScheduleErrors,
  } = useForm<EditScheduleForm>({
    defaultValues: {
      ...editScheduleFormDefault,
    },
    resolver: yupResolver(editScheduleFormSchema),
  });

  const handleEditScheduleSave = () => {
    handleEditScheduleSubmit((values) => {
      clearEditScheduleErrors('startDateTime');
      const currentSchedules = resourceWatch('schedules');

      const hasOverlap = currentSchedules?.some((existing: EditScheduleForm) =>
        scheduleOverlaps(values, existing)
      );

      if (hasOverlap) {
        setEditScheduleError('startDateTime', {
          type: 'manual',
          message:
            'This schedule overlaps with an existing one. Please choose a non-overlapping date range.',
        });
        return;
      }

      const updatedSchedules = currentSchedules
        ? [...currentSchedules, values]
        : [values];

      resourceSetValue('schedules', updatedSchedules, { shouldDirty: true });
      handleCloseEditSchedule();
      resetEditSchedule();
    })();
  };

  return (
    <SectionWithMeta
      title="Work Days"
      meta={() => (
        <Box hidden={!hasUpsertSchedulesPermissions}>
          <CreateNewEntityButton
            onClick={handleOpenEditSchedule}
            disabled={
              isUndefined(resourceWatch('ids')) || isEmpty(resourceWatch('ids'))
            }
            entityName="Schedule"
            testId="edit-schedule-button"
          />
        </Box>
      )}
    >
      <Modal
        testId="cert-popper-schedule"
        open={openEditSchedule}
        handleClickAway={handleCloseEditSchedule}
        title={'Work Days'}
      >
        <Form<EditScheduleForm>
          formId={'editScheduleForm'}
          errors={editScheduleFormState.errors}
          control={editScheduleControl}
          handleSubmit={handleEditScheduleSubmit}
          render={({ control, errors }: RenderFuncProps) => {
            return (
              <EditSchedule
                control={control}
                errors={errors}
                onSubmit={handleEditScheduleSave}
                upsertScheduleStatus={'success'}
                isEdit={isEdit}
                permissions={permissions}
              />
            );
          }}
        />
      </Modal>
      <Stack gap={2} direction="row">
        {renderAddedEvents}
      </Stack>
    </SectionWithMeta>
  );
};

export const RenderBlockedTimeSection = ({
  resourceWatch,
  resourceSetValue,
  handleCloseEditEvent,
  hasUpsertEventsPermissions,
  handleOpenEditEvent,
  openEditEvent,
  isEdit,
  renderAddedEvents,
  resourceCount,
}: RenderBlockedTimeSectionProps) => {
  const {
    control: editEventControl,
    formState: editEventFormState,
    handleSubmit: handleEditEventSubmit,
    reset: resetEditEvent,
    watch: watchEditEvent,
    getValues: getEditValues,
    setValue: setEditValue,
  } = useForm<EditEventForm>({
    defaultValues: {
      ...editEventFormDefault,
      days: {
        sunday: true,
        monday: true,
        tuesday: true,
        wednesday: true,
        thursday: true,
        friday: true,
        saturday: true,
      },
    },
    resolver: yupResolver(editEventFormSchema),
  });

  const handleEditEventSave = () => {
    handleEditEventSubmit((values) => {
      const currentEvents = resourceWatch('events');
      resourceSetValue('events', accumulateEvents(currentEvents, values), {
        shouldDirty: true,
      });
      handleCloseEditEvent();
      resetEditEvent();
    })();
  };

  return (
    <SectionWithMeta
      title="Blocked Time"
      meta={() => (
        <Box hidden={!hasUpsertEventsPermissions}>
          <CreateNewEntityButton
            disabled={
              isUndefined(resourceWatch('ids')) || isEmpty(resourceWatch('ids'))
            }
            onClick={handleOpenEditEvent}
            entityName="Event"
            testId="edit-event-button"
          />
        </Box>
      )}
    >
      <Modal
        testId="cert-popper"
        open={openEditEvent}
        handleClickAway={handleCloseEditEvent}
        title={'Blocked Time'}
      >
        <Form<EditEventForm>
          formId={'editEventForm'}
          errors={editEventFormState.errors}
          control={editEventControl}
          handleSubmit={handleEditEventSubmit}
          render={({ control, errors }: RenderFuncProps) => {
            return (
              <EditEvent
                control={control}
                errors={errors}
                onSubmit={handleEditEventSave}
                upsertEventStatus={'success'}
                isEdit={isEdit}
                hidePostalField
                isBulkUpdate={true}
                watchEditEvent={watchEditEvent}
                getEditValues={getEditValues}
                setEditValues={setEditValue}
              />
            );
          }}
        />
      </Modal>
      <Stack gap={2} direction="row">
        {renderAddedEvents}
      </Stack>
    </SectionWithMeta>
  );
};
